import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, Home, Wallet, Clock, CheckCircle2 } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import ApplicationForm from "@/components/forms/ApplicationForm";
import { IMAGES } from "@/data/site";
import { WFH } from "@/constants/testIds";

const perks = [
  { icon: Home, text: "100% remote — work from anywhere" },
  { icon: Clock, text: "Flexible hours around your schedule" },
  { icon: Wallet, text: "Genuine online earning opportunities" },
];

export default function WorkFromHome() {
  const [open, setOpen] = useState(false);

  return (
    <section
      id="work-from-home"
      data-testid={WFH.section}
      className="bg-white/[0.02] py-24 sm:py-28"
    >
      <div className="mx-auto max-w-6xl px-5 sm:px-8">
        <div className="grid items-stretch gap-8 lg:grid-cols-2">
          {/* Left copy */}
          <Reveal className="flex flex-col justify-center">
            <p className="font-display text-xs font-semibold uppercase tracking-[0.3em] text-brand">
              Work From Home Opportunities
            </p>
            <h2 className="mt-4 font-display text-3xl font-black leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
              Looking for remote work or online earning?
            </h2>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-neutral-400">
              If you're searching for genuine work-from-home and online earning
              opportunities, submit your details and our team will reach out with
              options that match your skills.
            </p>

            <ul className="mt-8 space-y-3">
              {perks.map((p) => (
                <li key={p.text} className="flex items-center gap-3 text-sm font-medium text-neutral-300">
                  <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-brand/20 bg-brand/10 text-brand">
                    <p.icon className="h-4 w-4" strokeWidth={1.9} />
                  </span>
                  {p.text}
                </li>
              ))}
            </ul>

            <div className="mt-10">
              <button
                data-testid={WFH.applyBtn}
                onClick={() => setOpen(true)}
                className="group inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-base font-semibold text-black shadow-[0_4px_14px_0_rgb(212,175,55,0.39)] transition-transform duration-200 hover:-translate-y-0.5 hover:bg-brand-hover"
              >
                Apply Now
                <ArrowUpRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </button>
            </div>
          </Reveal>

          {/* Right visual */}
          <Reveal delay={0.12} className="relative">
            <div className="relative h-full min-h-[360px] overflow-hidden rounded-3xl border border-white/10 shadow-[0_20px_60px_-24px_rgba(212,175,55,0.35)]">
              <img
                src={IMAGES.workFromHome}
                alt="Premium remote work desk setup"
                loading="lazy"
                className="h-full w-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="absolute bottom-5 left-5 right-5 flex items-center gap-3 rounded-2xl border border-white/10 bg-black/70 p-4 backdrop-blur-md"
              >
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-brand/10 text-brand">
                  <CheckCircle2 className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-sm font-semibold text-white">Quick & simple</p>
                  <p className="text-xs text-neutral-400">A short multi-step form — under 2 minutes.</p>
                </div>
              </motion.div>
            </div>
          </Reveal>
        </div>
      </div>

      <ApplicationForm open={open} onOpenChange={setOpen} />
    </section>
  );
}
